/* ==========================================================================
   BONPEK BABY - INFINITE SCROLL for collection and search grids   24 Sep 2026
   --------------------------------------------------------------------------
   Loaded from snippets/bonpek-grid-enhancements.liquid, on collection,
   search and list-collections pages only.

   A sentinel sits just above the pagination. When it nears the viewport the
   next page is fetched, its product cards are lifted out and appended to the
   live grid, and the process repeats until there is no next page.

   THE PAGINATION LINKS ARE NOT DELETED - they are hidden with a class and
   left in the DOM. They are how a crawler walks the collection. Infinite
   scroll that REMOVES the page-2 links is the commonest way a shop
   accidentally makes most of its catalogue unreachable to anything that
   cannot scroll. Do not change this to remove them.

   IT ALSO KEEPS SOLD-OUT PRODUCTS LAST, and this is half of a pair. The two
   section snippets render in-stock first and sold-out last, but Liquid only
   sees one paginate window, so page 2 arrives with its own sold-out tail.
   This merges them: in-stock cards are inserted ABOVE the existing sold-out
   tail, sold-out cards join it. Scrolled to the end, a collection has
   exactly one sold-out block, at the end. Remove one half and revisit the
   other.

   THE URL IS DELIBERATELY NOT REWRITTEN. Pushing ?page=3 as the customer
   scrolls looks clever and breaks refresh: reloading ?page=3 shows page 3
   ALONE, so the customer loses pages 1 and 2 and thinks the shop broke.

   🔴 THERE ARE TWO TRIGGERS, AND THE SECOND ONE IS NOT REDUNDANT.
   An IntersectionObserver samples on animation frames, so a scroll that
   JUMPS the sentinel - the End key, a browser-restored scroll position, an
   anchor link, a script calling scrollTo - can go from "sentinel below the
   viewport" to "sentinel above the viewport" without ever being sampled in
   between. The observer then never fires again, and because the pagination
   is hidden the customer is stranded at forty products with no way forward.
   Found in testing on 24 Sep, not in theory. The throttled scroll handler
   below re-checks the sentinel's real position and closes that hole. Keep
   both: the observer does the ordinary work cheaply, the handler catches
   the jumps.

   IF ANYTHING FAILS the pagination is un-hidden and the customer gets the
   normal numbered links back. It must never be possible to strand someone
   halfway through a collection.
   ========================================================================== */
(function () {
  'use strict';

  var GRID = '.collection-grid';
  var PAGINATION = '.pagination';
  var CARD = '.grid-product';
  var SOLD_OUT = '.grid-product__tag--sold-out';
  var LOOKAHEAD = 600; // px before the sentinel reaches the viewport
  var MAX_PAGES = 40;  // a stop so a broken next-link can never loop forever

  var state = null;
  var ticking = false;

  function isSoldOut(el) {
    return !!el.querySelector(SOLD_OUT);
  }

  /* first sold-out card currently in the grid - the top of the tail */
  function tailStart(grid) {
    var cards = grid.querySelectorAll(CARD);
    for (var i = 0; i < cards.length; i++) {
      if (isSoldOut(cards[i])) return cards[i];
    }
    return null;
  }

  function announce(msg) {
    if (state && state.live) state.live.textContent = msg;
  }

  function teardown(showPagination) {
    if (!state) return;
    if (state.observer) state.observer.disconnect();
    if (showPagination && state.pagination) {
      state.pagination.classList.remove('bpk-inf-hidden');
    }
    if (!showPagination && state.pagination && state.pagination.parentNode) {
      state.pagination.parentNode.removeChild(state.pagination);
    }
    if (state.sentinel && state.sentinel.parentNode) {
      state.sentinel.parentNode.removeChild(state.sentinel);
    }
    state = null;
  }

  function maybeLoad() {
    if (!state || state.busy || !state.next) return;
    var r = state.sentinel.getBoundingClientRect();
    if (r.top < window.innerHeight + LOOKAHEAD) load();
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      maybeLoad();
    });
  }

  function load() {
    if (!state || state.busy || !state.next) return;
    state.busy = true;
    state.sentinel.classList.add('is-loading');

    fetch(state.next, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (!state) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = doc.querySelector(GRID);
        if (!incoming) throw new Error('no grid in response');

        var cards = incoming.querySelectorAll(CARD);
        if (!cards.length) { announce('All products loaded.'); teardown(false); return; }

        var grid = state.grid;
        var tail = tailStart(grid);

        for (var i = 0; i < cards.length; i++) {
          var card = cards[i];
          if (isSoldOut(card) || !tail) {
            /* the sold-out tail always ends immediately before the sentinel */
            grid.insertBefore(card, state.sentinel);
          } else {
            grid.insertBefore(card, tail);
          }
        }

        state.loaded++;
        announce(cards.length + ' more products loaded.');

        try {
          sessionStorage.setItem('bpkInfPages:' + location.pathname + location.search, String(state.loaded));
        } catch (e) { /* private mode - position restore is a nicety, not a feature */ }

        var nextPag = incoming.querySelector(PAGINATION);
        var nextLink = nextPag ? nextPag.querySelector('.next a') : null;
        state.next = nextLink ? nextLink.getAttribute('href') : null;

        state.busy = false;
        state.sentinel.classList.remove('is-loading');

        if (!state.next || state.loaded >= MAX_PAGES) {
          announce('All products loaded.');
          teardown(false);
        } else {
          /* one batch may not fill a tall screen - check again immediately */
          maybeLoad();
        }
      })
      .catch(function () {
        teardown(true);
      });
  }

  function init() {
    if (state) teardown(true);

    var grid = document.querySelector(GRID);
    if (!grid) return;

    var pagination = grid.querySelector(PAGINATION);
    if (!pagination) return; // single page - nothing to do

    var nextLink = pagination.querySelector('.next a');
    if (!nextLink) return; // already on the last page

    if (!('IntersectionObserver' in window) || !window.fetch) return; // leave the links

    pagination.classList.add('bpk-inf-hidden');

    var sentinel = document.createElement('div');
    sentinel.className = 'bpk-inf-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.innerHTML = '<span class="bpk-inf-spinner"></span>';
    grid.insertBefore(sentinel, pagination);

    var live = document.querySelector('.bpk-inf-live');
    if (!live) {
      live = document.createElement('p');
      live.className = 'bpk-inf-live visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      grid.parentNode.insertBefore(live, grid.nextSibling);
    }

    state = {
      grid: grid,
      pagination: pagination,
      sentinel: sentinel,
      live: live,
      next: nextLink.getAttribute('href'),
      busy: false,
      loaded: 1
    };

    state.observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { load(); return; }
      }
    }, { rootMargin: LOOKAHEAD + 'px 0px' });
    state.observer.observe(sentinel);

    /* the page may already be scrolled past the sentinel on arrival */
    maybeLoad();
  }

  /* ------------------------------------------------------------------
     The theme's <item-grid> component swaps the whole of #AjaxContent
     when a filter or sort changes, which throws away our sentinel and
     hands us a fresh grid with fresh pagination. Watch for that and
     re-initialise. Debounced because the swap fires several mutations.
     ------------------------------------------------------------------ */
  function watchForGridSwaps() {
    var host = document.querySelector('#AjaxContent') || document.querySelector('item-grid');
    if (!host || !('MutationObserver' in window)) return;
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var grid = document.querySelector(GRID);
        if (!grid) return;
        if (state && state.grid === grid && document.body.contains(state.sentinel)) return;
        init();
      }, 120);
    }).observe(host, { childList: true, subtree: true });
  }

  function start() {
    init();
    watchForGridSwaps();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
